/* eslint-disable react/forbid-prop-types */
/* eslint-disable import/no-extraneous-dependencies */
import React, { useCallback } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import i18n from 'i18next';

import Document from '@tiptap/extension-document';
import Paragraph from '@tiptap/extension-paragraph';
import Text from '@tiptap/extension-text';
import { Placeholder } from '@tiptap/extensions';
import Bold from '@tiptap/extension-bold';
import Italic from '@tiptap/extension-italic';
import Heading from '@tiptap/extension-heading';
import { BulletList, OrderedList, ListItem } from '@tiptap/extension-list';
import Blockquote from '@tiptap/extension-blockquote';
import CodeBlock from '@tiptap/extension-code-block';
import HorizontalRule from '@tiptap/extension-horizontal-rule';
import Link from '@tiptap/extension-link';
import HardBreak from '@tiptap/extension-hard-break';
import { TextStyleKit } from '@tiptap/extension-text-style';

import PropTypes from 'prop-types';
import classnames from 'classnames';

import { createIconComponent } from 'uikit/icons';

import FormatBold from '@mui/icons-material/FormatBold';
import FormatItalic from '@mui/icons-material/FormatItalic';
import FormatClear from '@mui/icons-material/FormatClear';
import FormatListBulleted from '@mui/icons-material/FormatListBulleted';
import FormatListNumbered from '@mui/icons-material/FormatListNumbered';
import FormatQuote from '@mui/icons-material/FormatQuote';
import FormatCode from '@mui/icons-material/Code';
import FormatHorizontalRule from '@mui/icons-material/HorizontalRule';
import FormatLink from '@mui/icons-material/InsertLink';

import { useModals } from 'core/notifications';

import { Button } from 'uikit/buttons';
import { Tooltip } from 'uikit/tooltips';

import getLinkModal from '../helpers/getLinkModal';

import styles from '../styles/uikitTextEditorTextEditor.module.scss';

const Toolbar = ({ children = null }) => <div className={styles.toolbar}>{children}</div>;

const ToolbarButton = ({
  action = () => {},
  i18nKey = '',
  i18nFallback = '',
  icon = '',
  isActive = false,
  isDisabled = false,
}) => {
  const isUsingTooltip = i18nKey && i18nFallback;

  const button = (
    <Button
      aria-label={isUsingTooltip ? null : i18n.t(i18nKey, i18nFallback)}
      className={isActive ? styles.isButtonActive : ''}
      icon={icon && createIconComponent({ icon, size: 18 })}
      isDisabled={isDisabled}
      onClick={action}
      size="xsmall"
      type="button"
      variant="ghost"
    />
  );

  if (isUsingTooltip) {
    return (
      <Tooltip
        content={i18n.t(i18nKey, i18nFallback)}
        aria={{ content: 'labelledby' }}
        data-testid={`tooltip-${i18nKey || i18nFallback}`}
      >
        {button}
      </Tooltip>
    );
  }
  return button;
};

const TextEditor = ({
  content = '',
  id = '',
  isDisabled = false,
  onUpdateContent = () => {},
  options = [],
  placeholder = '',
}) => {
  const OPTION_EXTENSIONS = {
    code: CodeBlock,
    horizontalRule: HorizontalRule,
  };

  const optionExtensions = options
    .map((option) => OPTION_EXTENSIONS[option])
    .filter((ext) => ext !== undefined);

  const extensions = [
    Document,
    Paragraph,
    Text,
    ListItem,
    Placeholder.configure({
      placeholder,
      emptyEditorClass: styles.emptyEditor,
    }),
    Bold,
    Italic,
    BulletList,
    OrderedList,
    Blockquote,
    ...optionExtensions,
    Heading.configure({
      heading: {
        levels: [2, 3],
      },
    }),
    Link.configure({ openOnClick: false, defaultProtocol: 'https', protocols: ['http', 'https'] }),
    HardBreak,
    TextStyleKit,
  ];

  const editor = useEditor({
    extensions,
    content,
    autofocus: false,
    editable: !isDisabled,
    editorProps: {
      attributes: {
        'aria-labelledby': `${id}-label`,
        class: styles.editor,
      },
    },
    onUpdate: () => {
      onUpdateContent(editor.getHTML());
    },
  });

  const { addModal } = useModals();

  const handleLinkClick = useCallback(async () => {
    const { view, state } = editor;

    const getSelection = () => {
      const { from, to } = view.state.selection;
      return {
        from,
        to,
        text: state.doc.textBetween(from, to, ''),
      };
    };

    const { from, to, text } = getSelection();
    const link = editor.getAttributes('link');
    const { payload, type } = await addModal(getLinkModal({ target: '_blank', text, ...link }));

    if (type !== 'submit') return;

    editor
      .chain()
      .focus()
      .setLink(payload)
      .insertContentAt({ from, to }, payload.text, { updateSelection: true })
      .extendMarkRange('link')
      .run();
  }, [addModal, editor]);

  if (!editor) {
    return null;
  }

  return (
    <div className={classnames(styles.element, { [styles.isDisabled]: isDisabled })}>
      <Toolbar editor={editor} extensions={extensions}>
        <div className={styles.buttonGroup}>
          <ToolbarButton
            action={() => editor.chain().focus().toggleBold().run()}
            i18nKey="bold"
            i18nFallback="Bold"
            icon={<FormatBold />}
            isActive={editor.isActive('bold')}
            isDisabled={isDisabled}
          />
          <ToolbarButton
            action={() => editor.chain().focus().toggleItalic().run()}
            i18nKey="italic"
            i18nFallback="Italic"
            icon={<FormatItalic />}
            isActive={editor.isActive('italic')}
            isDisabled={isDisabled}
          />
          <ToolbarButton
            action={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
            i18nKey="subheading"
            i18nFallback="Subheading"
            icon="formatHeading2"
            isActive={editor.isActive('heading', { level: 2 })}
            isDisabled={isDisabled}
          />
          <ToolbarButton
            action={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
            i18nKey="subsubheading"
            i18nFallback="Subsubheading"
            icon="formatHeading3"
            isActive={editor.isActive('heading', { level: 3 })}
            isDisabled={isDisabled}
          />
          <ToolbarButton
            action={() => editor.chain().focus().unsetAllMarks().run()}
            i18nKey="clear-formatting"
            i18nFallback="Clear Formatting"
            icon={<FormatClear />}
            isDisabled={isDisabled}
          />
        </div>
        <div className={styles.buttonGroup}>
          <ToolbarButton
            action={() => editor.chain().focus().toggleBulletList().run()}
            i18nKey="bullet-list"
            i18nFallback="Bullet List"
            icon={<FormatListBulleted />}
            isActive={editor.isActive('bulletList')}
            isDisabled={isDisabled}
          />
          <ToolbarButton
            action={() => editor.chain().focus().toggleOrderedList().run()}
            i18nKey="numbered-list"
            i18nFallback="Numbered List"
            icon={<FormatListNumbered />}
            isActive={editor.isActive('orderedList')}
            isDisabled={isDisabled}
          />
        </div>
        <div className={styles.buttonGroup}>
          <ToolbarButton
            action={() => editor.chain().focus().toggleBlockquote().run()}
            i18nKey="quote"
            i18nFallback="Quote"
            icon={<FormatQuote />}
            isActive={editor.isActive('blockquote')}
            isDisabled={isDisabled}
          />
          {options.includes('code') && (
            <ToolbarButton
              action={() => editor.chain().focus().toggleCodeBlock().run()}
              i18nKey="code"
              i18nFallback="Code"
              icon={<FormatCode />}
              isActive={editor.isActive('codeBlock')}
              isDisabled={isDisabled}
            />
          )}
        </div>
        <div className={styles.buttonGroup}>
          {options.includes('horizontalRule') && (
            <ToolbarButton
              action={() => editor.chain().focus().setHorizontalRule().run()}
              i18nKey="horizontal-rule"
              i18nFallback="Horizontal Rule"
              icon={<FormatHorizontalRule />}
              isDisabled={isDisabled}
            />
          )}
          <ToolbarButton
            action={handleLinkClick}
            i18nKey="link"
            i18nFallback="Link"
            icon={<FormatLink />}
            isActive={editor.isActive('link')}
            isDisabled={isDisabled}
          />
        </div>
      </Toolbar>
      <EditorContent editor={editor} />
    </div>
  );
};

TextEditor.propTypes = {
  content: PropTypes.string,
  id: PropTypes.string,
  isDisabled: PropTypes.bool,
  onUpdateContent: PropTypes.func,
  options: PropTypes.arrayOf(PropTypes.oneOf(['code', 'horizontalRule'])),
  placeholder: PropTypes.string,
};

ToolbarButton.propTypes = {
  action: PropTypes.func,
  i18nKey: PropTypes.string,
  i18nFallback: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  isActive: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

Toolbar.propTypes = {
  children: PropTypes.node,
};

TextEditor.ToolbarButton = ToolbarButton;

export default TextEditor;
